import { useAuth } from '../auth/AuthProvider';
import { useMemo } from 'react';

// Use VITE_API_BASE when set (production), otherwise empty string for relative
// URLs which are proxied by Vite dev server in development.
const base = import.meta.env.VITE_API_BASE || '';

export interface ApiError extends Error { status: number; body?: any }

async function request(path: string, options: RequestInit, token?: string, onUnauthorized?: () => Promise<string | null>) {
  const isFormDataBody = typeof FormData !== 'undefined' && options.body instanceof FormData;
  const headers: Record<string,string> = { ...(options.headers as any) };
  if (!isFormDataBody && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }
  if (token) headers['Authorization'] = `Bearer ${token}`;
  // Admin "View as" role override
  const roleOverride = localStorage.getItem('roleOverride') || '';
  if (roleOverride && roleOverride !== 'admin') headers['X-Role-Override'] = roleOverride;
  const resp = await fetch(base + path, { ...options, headers });

  // On 401, attempt token refresh and retry once
  if (resp.status === 401 && onUnauthorized) {
    console.log('API 401 on', path, '— attempting token refresh');
    const newToken = await onUnauthorized();
    if (newToken) {
      const retryHeaders = { ...headers, 'Authorization': `Bearer ${newToken}` };
      const retry = await fetch(base + path, { ...options, headers: retryHeaders });
      if (!retry.ok) {
        const body = await retry.json().catch(() => undefined);
        const err = new Error(body?.detail || retry.statusText) as ApiError;
        err.status = retry.status; err.body = body;
        throw err;
      }
      if (retry.status === 204) return null;
      return retry.json();
    }
  }

  if (!resp.ok) {
    const body = await resp.json().catch(() => undefined);
    const err = new Error(body?.detail || resp.statusText) as ApiError;
    err.status = resp.status; err.body = body;
    throw err;
  }
  if (resp.status === 204) return null;
  return resp.json();
}

async function requestBlob(path: string, options: RequestInit, token?: string, onUnauthorized?: () => Promise<string | null>) {
  const headers: Record<string,string> = { ...(options.headers as any) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const roleOverride = localStorage.getItem('roleOverride') || '';
  if (roleOverride && roleOverride !== 'admin') headers['X-Role-Override'] = roleOverride;
  const resp = await fetch(base + path, { ...options, headers });

  if (resp.status === 401 && onUnauthorized) {
    const newToken = await onUnauthorized();
    if (newToken) {
      const retryHeaders = { ...headers, 'Authorization': `Bearer ${newToken}` };
      const retry = await fetch(base + path, { ...options, headers: retryHeaders });
      if (!retry.ok) {
        const body = await retry.json().catch(() => undefined);
        const err = new Error(body?.detail || retry.statusText) as ApiError;
        err.status = retry.status; err.body = body;
        throw err;
      }
      return retry.blob();
    }
  }

  if (!resp.ok) {
    const body = await resp.json().catch(() => undefined);
    const err = new Error(body?.detail || resp.statusText) as ApiError;
    err.status = resp.status; err.body = body;
    throw err;
  }
  return resp.blob();
}

export function useApi() {
  const { token, refreshToken } = useAuth();
  
  // Memoize API methods so they update when token changes
  return useMemo(() => ({
    get: (p: string, extraHeaders?: Record<string, string>) => request(p, { method: 'GET', headers: extraHeaders }, token || undefined, refreshToken),
    post: (p: string, body: any, extraHeaders?: Record<string, string>) => request(p, { method: 'POST', body: JSON.stringify(body), headers: extraHeaders }, token || undefined, refreshToken),
    postForm: (p: string, formData: FormData, extraHeaders?: Record<string, string>) => request(p, { method: 'POST', body: formData, headers: extraHeaders }, token || undefined, refreshToken),
    put: (p: string, body: any, extraHeaders?: Record<string, string>) => request(p, { method: 'PUT', body: JSON.stringify(body), headers: extraHeaders }, token || undefined, refreshToken),
    patch: (p: string, body: any, extraHeaders?: Record<string, string>) => request(p, { method: 'PATCH', body: JSON.stringify(body), headers: extraHeaders }, token || undefined, refreshToken),
    getBlob: (p: string, extraHeaders?: Record<string, string>) => requestBlob(p, { method: 'GET', headers: extraHeaders }, token || undefined, refreshToken),
    del: (p: string) => request(p, { method: 'DELETE' }, token || undefined, refreshToken),
    delete: (p: string) => request(p, { method: 'DELETE' }, token || undefined, refreshToken)
  }), [token, refreshToken]);
}

// Get token directly for components that need it
export function getAuthHeader(token: string | null): Record<string, string> {
  return token ? { 'Authorization': `Bearer ${token}` } : {};
}
